import {type TextElementOptions, TextUIElement, UI} from "./UIBase";
import {evaluateSprintf, isString} from "../base/Utils";

// Type definitions for translation functionality
interface TranslationMap {
    get(key: string): string | undefined;
}

// TODO @types use the proper LanguageStoreClass and other proper types. The Locale payload below is part of
// the contract - it is what localeChange carries - and stays bound to the event name until Callback's open
// arguments are replaced by the map described beside it
interface LanguageStore {
    getLocale(): Locale | null;
    addListener(event: string, callback: (language: Locale) => void): void;
}

interface Locale {
    buildTranslation(callback: (translationMap: TranslationMap) => void): void;
}


// This is the object that will be used to translate text
let translationMap: TranslationMap | null = null;

// Keep a set of all UI Element that need to be updated when the language changes
// Can't use a weak set here unfortunately because we need iteration
// That's why we must make sure to remove all nodes from the set when destroying them
export const TranslationElements = new Set<TranslationTextElement>();


// What a translatable holds: a plain string, or a sprintf format followed by the values to interpolate.
// The array cannot be a tuple: setValue builds it with Array.from(arguments)
export type TranslationValue = string | unknown[];

export class TranslationTextElement extends TextUIElement<TextElementOptions, TranslationValue> {
    constructor(value: TranslationValue) {
        if (arguments.length === 1) {
            super(value);
        } else {
            super("");
            // The callee is cast to a pure rest signature: `arguments` is not a tuple, and a spread cannot
            // reach a parameter list that starts with a required one
            (this.setValue as (...args: unknown[]) => void)(...arguments as any);
        }
    }

    // Variadic in practice: extra arguments are the sprintf values, read off `arguments` rather than declared.
    // Declaring a rest parameter here changes the emitted parameter list of a function that reads `arguments`,
    // and nothing in either repository calls this with more than one, so the signature stays as it is
    setValue(value: TranslationValue): void {
        if (arguments.length > 1) {
            this.value = Array.from(arguments);
        } else {
            this.value = value;
        }
        if (this.node) {
            this.redraw();
        }
    }

    evaluate(strings: string | string[], ...values: unknown[]): string {
        if (!Array.isArray(strings)) {
            strings = translationMap?.get(strings) || strings;
            return evaluateSprintf(strings, ...values);
            // This means strings is a string with the sprintf pattern
        } else {
            // Using template literals https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Template_literals
            if (arguments.length !== strings.length) {
                console.error("Invalid arguments to evaluate ", Array.from(arguments));
            }
            let result = strings[0];
            for (let i = 1; i < arguments.length; i++) {
                result += arguments[i];
                result += strings[i];
            }
            return result;
        }
    }

    getValue(): string {
        let {value} = this;
        if (Array.isArray(value)) {
            value = this.evaluate(...value as [string | string[], ...unknown[]]);
        } else {
            value = (translationMap && translationMap.get(value)) ?? value;
        }
        return value as string;
    }

    toString(): string {
        return this.getValue();
    }

    redraw(): void {
        if (!this.node) {
            this.node = this.createNode();
        }
        super.redraw();
    }

    onMount(): void {
        TranslationElements.add(this);
    }

    onUnmount(): void {
        TranslationElements.delete(this);
    }
}

// This method is a shorthand notation to create a new translatable text element
// TODO: should also support being used as a string template
UI.T = (str: string): TranslationTextElement => {
    return new TranslationTextElement(str);
};

// TODO @mciucu this should be wrapped in a way that previous requests that arrive later don't get processed
// TODO: should this be done with promises?
// Function to be called with a translation map
// The translationMap object needs to implement .get(value) to return the translation for value
function setTranslationMap(_translationMap: TranslationMap): void {
    if (translationMap === _translationMap) {
        return;
    }
    translationMap = _translationMap;
    for (let textElement of TranslationElements.values()) {
        textElement.redraw();
    }
}

let languageStore: LanguageStore | null = null;

// The store has to answer getLocale() and raise localeChange, and its locales have to build a translation
// map through the callback buildTranslation is given
export function setLanguageStore(_languageStore: LanguageStore): void {
    languageStore = _languageStore;

    let currentLocale = languageStore.getLocale();
    // If there's a default language already set, build the translation table for it
    if (currentLocale) {
        currentLocale.buildTranslation(setTranslationMap);
    }

    // Add a listener for whenever the language changes
    languageStore.addListener("localeChange", (language: Locale) => {
        language.buildTranslation(setTranslationMap);
    });
}

export function getTranslationMap(): TranslationMap | null {
    return translationMap;
}

// TODO @Mihai why is this here?
if (!document.startViewTransition) {
    // A no-op stand-in for browsers without the API, which never returns the ViewTransition nobody here reads
    document.startViewTransition = ((callback: () => void) => callback()) as typeof document.startViewTransition;
}

